import { z } from "zod";

/**
 * Build-time configuration every HoW browser app reads. Vite inlines only VITE_* keys, so nothing here can be a
 * secret; the Supabase publishable key is public by design and RLS protects the data.
 */
export const appEnvSchema = z.object({
  VITE_SUPABASE_URL: z.url(),
  VITE_SUPABASE_PUBLISHABLE_KEY: z.string().min(20),
  /** Platform base domain the tenant hosts hang off, e.g. staging.how.dcxlogic.com. */
  VITE_HOW_PLATFORM_BASE_DOMAIN: z.string().min(3).default("how.dcxlogic.com"),
  /** Local development only: resolve the tenant as if the page were served on this hostname. */
  VITE_HOW_DEV_TENANT_HOST: z.string().min(3).optional(),
  /** Mirrors the platform_settings.require_platform_mfa rule so the control plane can force enrollment up front. */
  VITE_HOW_REQUIRE_PLATFORM_MFA: z.boolean().default(false),
  /**
   * Staff dashboard only (ADR 0065): forward the old addresses (`<slug>.dash.`, `login.`, `app.`) to the staff door.
   * Off by default; turned on per environment once the door's DNS and Railway domains exist (runbook staff-door-dns).
   */
  VITE_HOW_STAFF_DOOR_FORWARD: z.boolean().default(false),
});

export type AppEnv = z.infer<typeof appEnvSchema>;

/** Vite's import.meta.env index signature is untyped; pass it in as an unknown record. */
export function readAppEnv(raw: Record<string, unknown>): AppEnv {
  return appEnvSchema.parse({
    VITE_SUPABASE_URL: raw["VITE_SUPABASE_URL"],
    VITE_SUPABASE_PUBLISHABLE_KEY: raw["VITE_SUPABASE_PUBLISHABLE_KEY"],
    VITE_HOW_PLATFORM_BASE_DOMAIN: raw["VITE_HOW_PLATFORM_BASE_DOMAIN"] ?? undefined,
    VITE_HOW_DEV_TENANT_HOST: raw["VITE_HOW_DEV_TENANT_HOST"] || undefined,
    VITE_HOW_REQUIRE_PLATFORM_MFA:
      raw["VITE_HOW_REQUIRE_PLATFORM_MFA"] === "true" ||
      raw["VITE_HOW_REQUIRE_PLATFORM_MFA"] === "1",
    VITE_HOW_STAFF_DOOR_FORWARD:
      raw["VITE_HOW_STAFF_DOOR_FORWARD"] === "true" || raw["VITE_HOW_STAFF_DOOR_FORWARD"] === "1",
  });
}
